import React, {useEffect, useState} from "react";
import axios from "config/axiosConfig";
import {useLocation, useNavigate} from "react-router-dom";
import {formatDate} from "../util/helpers";

export default function PaymentDetail() {
    const [detail, setDetail] = useState();
    const [adjustAmount, setAdjustAmount] = useState("");
    const [adjustReason, setAdjustReason] = useState("");
    const [adjustedBy, setAdjustedBy] = useState("");
    const [adjustments, setAdjustments] = useState([]);
    const [submitState, setSubmitState] = useState({loading: false, error: null, success: null});

    const navigate = useNavigate();
    const location = useLocation();

    const queryParams = new URLSearchParams(location.search);
    const id = queryParams.get("id");

    const requestDetail = async () => {
        try {
            const payment = await axios.get(`/admin/v1/payments/${id}`).then(res => res.data);
            setDetail(payment);

            const adjustmentRows = await axios.get(`/admin/v1/wallet/transactions/adjustments`, {
                params: { paymentId: payment.id }
            }).then(res => res.data || []);
            setAdjustments(adjustmentRows);
        } catch (error) {
            setSubmitState({
                loading: false,
                error: error?.response?.data?.detail || "결제 정보를 불러오는 중 오류가 발생했습니다.",
                success: null
            });
        }
    };

    const displayPaymentStatus = (status, settlementDueAt, settlementAt) => {
        if (status === 'PAID' && settlementAt !== null) {
            return '지급 확정'
        }
        if (status === 'PAID' && settlementDueAt !== null) {
            return '지급 대기'
        }
        switch (status) {
            case 'PAID': return '결제 완료';
            case 'PARTIAL_CANCEL': return '부분 지급';
            case 'CANCELLED': return '지급 취소';
            default: return status;
        }
    };

    useEffect(() => {
        if (id) requestDetail();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleAdjustmentSubmit = async (event) => {
        event.preventDefault();
        if (!detail) return;
        const amountValue = parseInt(adjustAmount, 10);
        if (Number.isNaN(amountValue) || amountValue === 0) {
            setSubmitState({loading: false, error: "조정 금액은 0이 아닌 숫자여야 합니다.", success: null});
            return;
        }
        if (!adjustReason.trim()) {
            setSubmitState({loading: false, error: "조정 사유를 입력해주세요.", success: null});
            return;
        }

        try {
            setSubmitState({loading: true, error: null, success: null});
            const res = await axios.post(`/admin/v1/wallet/payments/${detail.id}/adjust`, {
                amount: amountValue,
                reason: adjustReason,
                adjustedBy: adjustedBy?.trim() || null
            });
            setAdjustments(res.data || []);
            setSubmitState({loading: false, error: null, success: "조정이 완료되었습니다."});
            setAdjustAmount("");
            setAdjustReason("");
            requestDetail();
        } catch (err) {
            setSubmitState({
                loading: false,
                error: err?.response?.data?.detail || "조정 요청 중 오류가 발생했습니다.",
                success: null
            });
        }
    };

    return (
        <div className="App">
            <div className="wrapper">
                <div className="content-wrapper" style={{marginLeft: '0px'}}>
                    <div className="content-header">
                        <div className="container-fluid">
                            <div className="row mb-2">
                                <div className="col-sm-12">
                                    <ol className="breadcrumb">
                                        <li className="breadcrumb-item active">결제관리 &gt; 결제 이력 &gt; 상세</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>

                    <section className="content">
                        <div className="container-fluid">
                            <div className="row">
                                <div className="col-12">
                                    <div className="card">
                                        <div className="card-header">
                                            <h3 className="card-title" style={{fontWeight: 'bold'}}>결제 정보</h3>
                                        </div>
                                        <div className="card-body table-responsive p-0">
                                            <table className="table table-hover text-nowrap">
                                                {detail && (
                                                    <tbody>
                                                    <tr>
                                                        <th style={{width: '140px'}}>결제ID</th>
                                                        <td>{detail.id}</td>
                                                        <th style={{width: '140px'}}>결제 상태</th>
                                                        <td>{displayPaymentStatus(detail.status, detail.settlementDueAt, detail.settlementAt)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>매칭ID</th>
                                                        <td>
                                                            <a href={`/page/chat/detail?id=${detail.chatRoomId}`}
                                                               target="_blank"
                                                               rel="noopener noreferrer">{detail.chatRoomId}</a>
                                                        </td>
                                                        <th>결제 금액</th>
                                                        <td>{detail.paidAmount?.toLocaleString?.() || detail.paidAmount || '-'}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>매칭 일시</th>
                                                        <td>{formatDate(detail.chatCreatedAt)}</td>
                                                        <th>결제 일시</th>
                                                        <td>{formatDate(detail.paidAt)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>환불 금액</th>
                                                        <td>{detail.refundAmount?.toLocaleString?.() || detail.refundAmount || '-'}</td>
                                                        <th>환불 일시</th>
                                                        <td>{formatDate(detail.refundAt)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>{ detail.settlementAt === null ? '정산 예정 금액' : '정산 금액' }</th>
                                                        <td>{detail.settlementAmount?.toLocaleString?.() || detail.settlementAmount || '-'}</td>
                                                        <th>정산 예정 일시</th>
                                                        <td>{formatDate(detail.settlementDueAt)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>정산 일시</th>
                                                        <td colSpan={3}>{formatDate(detail.settlementAt)}</td>
                                                    </tr>
                                                    <tr>
                                                        <th>{ detail.settlementAt === null ? '수수료 예정 금액' : '수수료 금액' }</th>
                                                        <td colSpan={3}>{detail.commissionAmount?.toLocaleString?.() || detail.commissionAmount || '-'}</td>
                                                    </tr>
                                                    </tbody>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {detail && (
                                <div className="row">
                                    <div className="col-12">
                                        <div className="card" style={{marginTop: '20px'}}>
                                            <div className="card-header">
                                                <h3 className="card-title" style={{fontWeight: 'bold'}}>정산 수수료 조정</h3>
                                            </div>
                                            <div className="card-body">
                                                <form onSubmit={handleAdjustmentSubmit}>
                                                    <div className="form-row" style={{display: 'flex', flexWrap: 'wrap', gap: '10px'}}>
                                                        <div className="form-group" style={{flex: '1 1 180px'}}>
                                                            <label>조정 금액 (현재수수료 기준으로 + or - 됩니다.)</label>
                                                            <input
                                                                type="number"
                                                                className="form-control"
                                                                value={adjustAmount}
                                                                onChange={e => setAdjustAmount(e.target.value)}
                                                                placeholder="예: 1000 또는 -1000"
                                                            />
                                                        </div>
                                                        <div className="form-group" style={{flex: '2 1 240px'}}>
                                                            <label>조정 사유</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={adjustReason}
                                                                onChange={e => setAdjustReason(e.target.value)}
                                                                placeholder="사용자 문의, 수수료 보정 등"
                                                            />
                                                        </div>
                                                        <div className="form-group" style={{flex: '1 1 200px'}}>
                                                            <label>조정 담당자</label>
                                                            <input
                                                                type="text"
                                                                className="form-control"
                                                                value={adjustedBy}
                                                                onChange={e => setAdjustedBy(e.target.value)}
                                                                placeholder="관리자 이름 (선택)"
                                                            />
                                                        </div>
                                                        <div className="form-group" style={{alignSelf: 'flex-end'}}>
                                                            <button
                                                                type="submit"
                                                                className="btn btn-primary"
                                                                disabled={submitState.loading}
                                                            >
                                                                {submitState.loading ? "조정 중..." : "조정 요청"}
                                                            </button>
                                                        </div>
                                                    </div>
                                                </form>
                                                {submitState.error && (
                                                    <div className="alert alert-danger" role="alert" style={{marginTop: '15px'}}>
                                                        {submitState.error}
                                                    </div>
                                                )}
                                                {submitState.success && (
                                                    <div className="alert alert-success" role="alert" style={{marginTop: '15px'}}>
                                                        {submitState.success}
                                                    </div>
                                                )}

                                                <div style={{marginTop: '20px'}}>
                                                    <h5 style={{fontWeight: 'bold'}}>최근 조정 내역</h5>
                                                    <table className="table table-hover text-nowrap" style={{marginTop: '10px'}}>
                                                        <thead>
                                                        <tr>
                                                            <th>거래ID</th>
                                                            <th>회원 아이디</th>
                                                            <th>금액</th>
                                                            <th>설명</th>
                                                            <th>조정 등록 시각</th>
                                                        </tr>
                                                        </thead>
                                                        <tbody>
                                                        {adjustments.length === 0 && (
                                                            <tr>
                                                                <td colSpan={6} style={{textAlign: 'center'}}>조정 내역이 없습니다.</td>
                                                            </tr>
                                                        )}
                                                        {adjustments.map(row => (
                                                            <tr key={row.id}>
                                                                <td>{row.id}</td>
                                                                <td>
                                                                    <a
                                                                            href={`/page/users/detail?userId=${row.memberId}`}
                                                                            target="_blank"
                                                                            rel="noopener noreferrer"
                                                                    >
                                                                        {row.memberId === 1 ? '(시스템)' : row.memberId || '-'}
                                                                    </a>
                                                                </td>
                                                                <td>{row.amount?.toLocaleString?.() || row.amount}</td>
                                                                <td>{row.description || '-'}</td>
                                                                <td>{formatDate(row.createdAt)}</td>
                                                            </tr>
                                                        ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="row" style={{marginTop: '30px', marginBottom: '10px'}}>
                                <div style={{margin: "0 auto"}}>
                                    <button type="button" className="btn btn-outline-dark" onClick={() => navigate(-1)}>
                                        목록
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>
            </div>
        </div>
    );
}
